/**
 * The galaxy field mix for M2's rocks (M8 plan MC-a5a, review 1 M1, review 3 m-2). Streamed clusters merge into one
 * `sim.field`, a system has 1–4 fields and `FieldLayout` has no field column, so the rock runtime asks per position:
 * `fieldMixAt(x, y, z, out)` writes the ore shares of the field the rock sits in (`mineralWeights ?? oreWeights`,
 * normalised, in M2 `WARE_IDS` order) and returns that field's grade multiplier (`galaxy.json minerals.grades.mult`).
 *
 * - A rock is in a field when it lies within the field radius plus the reach of one M1 cluster envelope (cluster
 *   centres sit inside the radius, their rocks can stick out); overlapping or adjacent fields pick the nearest centre.
 * - Outside every field it writes zeros and returns 1: archetype only.
 * - Inside an anomaly hotspot cluster (MC-a9) it writes the system's signature hotspot mix and returns 1: the
 *   system's signature-weighted asteroid mix (`mineralWeights`, the same in every asteroid field of a system) with the
 *   signature ore × `galaxy.json minerals.hotspot.mult`, renormalised, then clamped so no ore's share exceeds
 *   `minerals.stackCap` × its region band share (T18, `clampShares`). The signature ore is the one whose
 *   `mineralWeights` share stands highest over its band share (signature × star bias is largest on the primary; the
 *   layout carries no signature). Tetherstone stays 0 there: a hotspot's tetherstone is the fracture roll
 *   (`hotspotTetherChance`). A system without a `mineralWeights` field (pre-M8 layout, no asteroid field) keeps zeros.
 *
 * - The scanner's signature line (MC-a6, PF-4) asks per position too (`signatureAt`): a field "holds" the signature
 *   ore its rocks are made of. An asteroid field's signature-weighted mix (`mineralWeights`) and a hotspot hold the
 *   system's primary signature; an ice or gas field (its `oreWeights` mix, no signature weighting) holds its main ore
 *   only when that ore is one of the system's signature ores (`layout.signature` primary or secondary, or the
 *   inferred primary), else none. So an ice field in a Ferrite · Ice system reads ICE, never FERRITE.
 *
 * Built once per system entry (load-time, allocates); the returned lookup reads fixed typed arrays and allocates
 * nothing (D-014). The rocks blend it at `mining.json mix.fieldBlend` (MC-a5b), which ships 0 until MC-a6.
 */
import { galaxyGradeIds, loadGalaxyConfig, type GalaxyConfig } from '../../data/galaxyConfig'
import { TETHERSTONE_INDEX, WARE_COUNT, WARE_IDS } from '../../data/m2Schemas'
import type { FieldConfig } from '../../data/schemas'
import type { FieldSite, SystemLayout } from '../../galaxy/layout/types'
import { clampShares } from '../../galaxy/minerals'
import type { RegionId, SystemSignature } from '../../galaxy/types'
import type { FieldMixAt } from '../../space/contracts'
import { clusterBounds, hotspotCluster } from '../../streaming'

type MineralsCfg = GalaxyConfig['minerals']
/** The hotspot rule's inputs from `galaxy.json minerals`. */
export type HotspotMinerals = Pick<MineralsCfg, 'regionBands' | 'hotspot' | 'stackCap'>

const REFRACTORY_ORE = 'refractory_ore'
let cachedMinerals: MineralsCfg | null = null
function minerals(): MineralsCfg {
  cachedMinerals ??= loadGalaxyConfig().minerals
  return cachedMinerals
}

/** `galaxy.json minerals.grades.mult`, poorest first (index = `galaxyGradeIds`). */
export function defaultGradeMult(): readonly number[] {
  return minerals().grades.mult
}

/** `galaxy.json minerals` (cached). */
export function defaultMinerals(): MineralsCfg {
  return minerals()
}

/**
 * The layout fields a system's field mix needs; `region` picks the hotspot's band (absent: no hotspot mix) and
 * `signature` is the galaxy's signature ore(s) of the system (absent: only the inferred primary is known).
 */
export type FieldMixLayout = Pick<SystemLayout, 'fields' | 'pois'> & { readonly region?: RegionId; readonly signature?: SystemSignature }

/** The inferred signature (see the file comment): the ore shares, the band and the signature's index in `ores`. */
interface SignatureBase {
  readonly ores: readonly (typeof WARE_IDS)[number][]
  readonly band: readonly number[]
  readonly base: readonly number[]
  readonly sig: number
}

function signatureBase(layout: FieldMixLayout, m: HotspotMinerals): SignatureBase | null {
  const region = layout.region
  if (region === undefined) return null
  const source = layout.fields.find((f) => f.mineralWeights !== undefined)?.mineralWeights
  if (source === undefined) return null
  const ores = WARE_IDS.filter((_, w) => w !== TETHERSTONE_INDEX)
  const bandRaw = ores.map((o) => m.regionBands[region][o] ?? 0)
  const bandTotal = bandRaw.reduce((s, v) => s + v, 0)
  const baseRaw = ores.map((o) => source[o] ?? 0)
  const baseTotal = baseRaw.reduce((s, v) => s + v, 0)
  if (!(bandTotal > 0 && baseTotal > 0)) return null
  const band = bandRaw.map((v) => v / bandTotal)
  const base = baseRaw.map((v) => v / baseTotal)
  let sig = -1
  let sigRatio = 0
  // 5(e) (MC-e2): the refractory ore rolls on its own stream after the M8 signature (galaxy/minerals.ts applyRefractory),
  // so it is never the inferred (hotspot) signature: the M8 primary stays the hotspot ore, as in a v6 world.
  for (let i = 0; i < base.length; i++) {
    if (ores[i] === REFRACTORY_ORE) continue
    const ratio = band[i] > 0 ? base[i] / band[i] : 0
    if (ratio > sigRatio) {
      sig = i
      sigRatio = ratio
    }
  }
  return sig < 0 ? null : { ores, band, base, sig }
}

/** The system's signature ore as an M2 ware index (the scanner's signature line, MC-a6), or -1 without one. Load-time. */
export function signatureWareOf(layout: FieldMixLayout, m: HotspotMinerals = minerals()): number {
  const b = signatureBase(layout, m)
  return b === null ? -1 : WARE_IDS.indexOf(b.ores[b.sig])
}

/**
 * The signature hotspot mix of a system in `WARE_IDS` order (see the file comment), or null when the system has no
 * `mineralWeights` field or no region. Load-time; allocates.
 */
export function signatureHotspotMix(layout: FieldMixLayout, m: HotspotMinerals = minerals()): Float32Array | null {
  const b = signatureBase(layout, m)
  if (b === null) return null
  const { ores, band, base, sig } = b
  const raw = base.map((v, i) => (i === sig ? v * m.hotspot.mult : v))
  const rawTotal = raw.reduce((s, v) => s + v, 0)
  const shares = clampShares(
    raw.map((v) => v / rawTotal),
    band,
    m.stackCap,
  )
  const out = new Float32Array(WARE_COUNT)
  ores.forEach((o, i) => {
    out[WARE_IDS.indexOf(o)] = shares[i]
  })
  return out
}

/**
 * The signature ore (ware index) a field holds, or -1 (see the file comment): `primary` for a signature-weighted
 * asteroid mix, else the field's main ore when it is in `signatureWares`. Load-time.
 */
export function fieldSignatureOf(field: Pick<FieldSite, 'oreWeights' | 'mineralWeights'>, primary: number, signatureWares: readonly number[]): number {
  if (field.mineralWeights !== undefined) return primary
  let top = -1
  let topW = 0
  for (let w = 0; w < WARE_COUNT; w++) {
    const v = w === TETHERSTONE_INDEX ? 0 : (field.oreWeights[WARE_IDS[w]] ?? 0)
    if (v > topW) {
      top = w
      topW = v
    }
  }
  return top >= 0 && signatureWares.includes(top) ? top : -1
}

/** The system's signature ores as ware indices: `layout.signature` primary and secondary, plus the inferred primary. */
function signatureWaresOf(layout: FieldMixLayout, primary: number): number[] {
  const out = primary >= 0 ? [primary] : []
  const s = layout.signature
  for (const ore of s === undefined ? [] : [s.primary, s.secondary]) {
    const w = ore === undefined ? -1 : WARE_IDS.indexOf(ore)
    if (w >= 0 && !out.includes(w)) out.push(w)
  }
  return out
}

/** A field's grade multiplier: 1 when the field has no grade (ice, gas, or a pre-M8 layout). */
export function gradeMultOf(field: Pick<FieldSite, 'grade'>, gradeMult: readonly number[]): number {
  if (field.grade === undefined) return 1
  return gradeMult[galaxyGradeIds.indexOf(field.grade)] ?? 1
}

/** How far a cluster's rocks can reach past its field's radius: the M1 envelope's offset plus its largest semi-axis. */
function clusterReach(fieldCfg: FieldConfig): number {
  const e = fieldCfg.envelope
  return Math.hypot(e.center[0], e.center[1], e.center[2]) + Math.max(e.semiAxes[0], e.semiAxes[1], e.semiAxes[2])
}

/** One system's field mix, in fixed typed arrays filled on system entry. */
export interface FieldMixTable {
  /** Field count, then per field: centre (3 × float64, system frame), squared reach, `WARE_COUNT` shares, grade multiplier. */
  readonly fields: number
  readonly centres: Float64Array
  readonly reach2: Float64Array
  readonly shares: Float32Array
  readonly grades: Float64Array
  /** Anomaly hotspot clusters: centre (3 × float64) and squared radius. */
  readonly hotspots: number
  readonly hotCentres: Float64Array
  readonly hotR2: Float64Array
  /** The signature hotspot mix (`WARE_COUNT` shares), written inside every hotspot; null keeps zeros there (MC-a9). */
  readonly hotShares: Float32Array | null
  /** Per field, the signature ore it holds (`fieldSignatureOf`, -1: none); `hotSignature` inside a hotspot (PF-4). */
  readonly fieldSignature: Int16Array
  readonly hotSignature: number
  /** Per field, the ore its rocks keep through the `maxOres` cut (MC-e6b, -1: none); `hotKeep` inside a hotspot. */
  readonly fieldKeep: Int16Array
  readonly hotKeep: number
}

/** Builds a system's table (see the file comment). Load-time; allocates. */
export function buildFieldMixTable(
  layout: FieldMixLayout,
  fieldCfg: FieldConfig,
  gradeMult: readonly number[] = defaultGradeMult(),
  m: HotspotMinerals = minerals(),
): FieldMixTable {
  const fields = layout.fields
  const nf = fields.length
  const centres = new Float64Array(nf * 3)
  const reach2 = new Float64Array(nf)
  const shares = new Float32Array(nf * WARE_COUNT)
  const grades = new Float64Array(nf)
  const pad = clusterReach(fieldCfg)
  const primary = signatureWareOf(layout, m)
  const sigWares = signatureWaresOf(layout, primary)
  const fieldSignature = new Int16Array(nf)
  const fieldKeep = new Int16Array(nf).fill(-1)
  // MC-e6b: a refractory-signature system's fields keep the ore through the `maxOres` cut wherever they hold it.
  const keepWare = layout.signature?.refractory === true ? WARE_IDS.indexOf(REFRACTORY_ORE) : -1
  for (let f = 0; f < nf; f++) {
    const field = fields[f]
    centres[f * 3] = field.center[0]
    centres[f * 3 + 1] = field.center[1]
    centres[f * 3 + 2] = field.center[2]
    const r = field.radiusM + pad
    reach2[f] = r * r
    const weights = field.mineralWeights ?? field.oreWeights
    let total = 0
    for (let w = 0; w < WARE_COUNT; w++) total += weights[WARE_IDS[w]] ?? 0
    if (total > 0) for (let w = 0; w < WARE_COUNT; w++) shares[f * WARE_COUNT + w] = (weights[WARE_IDS[w]] ?? 0) / total
    grades[f] = gradeMultOf(field, gradeMult)
    fieldSignature[f] = fieldSignatureOf(field, primary, sigWares)
    if (keepWare >= 0 && shares[f * WARE_COUNT + keepWare] > 0) fieldKeep[f] = keepWare
  }
  const anomalies = layout.pois.filter((p) => p.kind === 'anomaly')
  const na = anomalies.length
  const hotCentres = new Float64Array(na * 3)
  const hotR2 = new Float64Array(na)
  for (let a = 0; a < na; a++) {
    const b = clusterBounds(hotspotCluster(anomalies[a], fieldCfg).site, fieldCfg)
    hotCentres[a * 3] = b.center[0]
    hotCentres[a * 3 + 1] = b.center[1]
    hotCentres[a * 3 + 2] = b.center[2]
    hotR2[a] = b.radiusM * b.radiusM
  }
  const hotShares = na > 0 ? signatureHotspotMix(layout, m) : null
  const hotSignature = hotShares === null ? -1 : primary
  const hotKeep = keepWare >= 0 && hotShares !== null && hotShares[keepWare] > 0 ? keepWare : -1
  return { fields: nf, centres, reach2, shares, grades, hotspots: na, hotCentres, hotR2, hotShares, fieldSignature, hotSignature, fieldKeep, hotKeep }
}

/** Where (x, y, z) sits: `HOTSPOT` inside a hotspot cluster, else the nearest field in reach, or -1. Allocation-free. */
const HOTSPOT = -2
function locate(t: FieldMixTable, x: number, y: number, z: number): number {
  const hc = t.hotCentres
  for (let a = 0; a < t.hotspots; a++) {
    const dx = x - hc[a * 3]
    const dy = y - hc[a * 3 + 1]
    const dz = z - hc[a * 3 + 2]
    if (dx * dx + dy * dy + dz * dz <= t.hotR2[a]) return HOTSPOT
  }
  const c = t.centres
  let best = -1
  let bestD2 = Infinity
  for (let f = 0; f < t.fields; f++) {
    const dx = x - c[f * 3]
    const dy = y - c[f * 3 + 1]
    const dz = z - c[f * 3 + 2]
    const d2 = dx * dx + dy * dy + dz * dz
    if (d2 <= t.reach2[f] && d2 < bestD2) {
      best = f
      bestD2 = d2
    }
  }
  return best
}

/**
 * The lookup: writes the shares of the field at (x, y, z) into `out[0..WARE_COUNT)` and returns its grade multiplier;
 * inside a hotspot cluster the signature hotspot mix (zeros without one) and 1; zeros and 1 outside every field.
 * Allocation-free (D-014).
 */
export function writeFieldMix(t: FieldMixTable, x: number, y: number, z: number, out: Float32Array): number {
  out.fill(0, 0, WARE_COUNT)
  const best = locate(t, x, y, z)
  if (best === HOTSPOT) {
    const hot = t.hotShares
    if (hot !== null) for (let w = 0; w < WARE_COUNT; w++) out[w] = hot[w]
    return 1
  }
  if (best < 0) return 1
  for (let w = 0; w < WARE_COUNT; w++) out[w] = t.shares[best * WARE_COUNT + w]
  return t.grades[best]
}

/** The signature ore the field (or hotspot) at (x, y, z) holds, -1 outside every field or for none. Allocation-free. */
export function signatureAtTable(t: FieldMixTable, x: number, y: number, z: number): number {
  const at = locate(t, x, y, z)
  return at === HOTSPOT ? t.hotSignature : at < 0 ? -1 : t.fieldSignature[at]
}

/** The ore the field (or hotspot) at (x, y, z) keeps through the `maxOres` cut (MC-e6b), -1 for none. Allocation-free. */
export function keepAtTable(t: FieldMixTable, x: number, y: number, z: number): number {
  const at = locate(t, x, y, z)
  return at === HOTSPOT ? t.hotKeep : at < 0 ? -1 : t.fieldKeep[at]
}

/** The rock runtime's hook for a system: one closure per system entry over its table; each call is allocation-free. */
export function fieldMixOf(
  layout: FieldMixLayout,
  fieldCfg: FieldConfig,
  gradeMult: readonly number[] = defaultGradeMult(),
  m: HotspotMinerals = minerals(),
): FieldMixAt {
  const table = buildFieldMixTable(layout, fieldCfg, gradeMult, m)
  const at = (x: number, y: number, z: number, out: Float32Array): number => writeFieldMix(table, x, y, z, out)
  const signatureAt = (x: number, y: number, z: number): number => signatureAtTable(table, x, y, z)
  const keepAt = (x: number, y: number, z: number): number => keepAtTable(table, x, y, z)
  return Object.assign(at, { signature: signatureWareOf(layout, m), signatureAt, keepAt })
}
